import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Copy, LifeBuoy } from 'lucide-react';
import { toast as notifications } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { copyableIncident, supportStore } from '@/lib/error-support';
import { installFormErrorFeedback } from '@/lib/form-errors';
import { useLocation } from 'wouter';

export function SupportFeedback() {
  const incidents = useSyncExternalStore(supportStore.subscribe, supportStore.snapshot);
  const [open, setOpen] = useState(false);
  const [copyStatus, setCopyStatus] = useState('');
  const [hasDialog, setHasDialog] = useState(false);
  const [location] = useLocation();
  const [noticeVisible, setNoticeVisible] = useState(true);
  useEffect(() => {
    setNoticeVisible(false);
    setOpen(false);
  }, [location]);
  useEffect(() => {
    if (incidents.length) setNoticeVisible(true);
  }, [incidents]);
  const opener = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (open)
      notifications
        .getHistory()
        .filter((item) => 'type' in item && item.type === 'error')
        .forEach((item) => notifications.dismiss(item.id));
  }, [open]);
  useEffect(() => installFormErrorFeedback(), []);
  useEffect(() => {
    const observe = () =>
      setHasDialog(Boolean(document.querySelector('[role="dialog"]:not([data-error-support])')));
    const observer = new MutationObserver(observe);
    observer.observe(document.body, { childList: true, subtree: true });
    observe();
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    const show = () => {
      opener.current =
        document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setCopyStatus('');
      setOpen(true);
    };
    const clear = () => {
      supportStore.clear();
      setOpen(false);
    };
    window.addEventListener('chefiapro-open-error-support', show);
    window.addEventListener('chefiapro-session-ended', clear);
    return () => {
      window.removeEventListener('chefiapro-open-error-support', show);
      window.removeEventListener('chefiapro-session-ended', clear);
    };
  }, []);
  if (!incidents.length) return null;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          style={hasDialog || open || !noticeVisible ? { display: 'none' } : undefined}
          className="fixed bottom-4 right-4 z-[70] max-w-[calc(100vw-2rem)] bg-[rgb(var(--card))] shadow-lg"
          onClick={() => setOpen(true)}
        >
          <LifeBuoy /> Ajuda sobre o erro · {incidents[0]!.support.code}
        </Button>
      </DialogTrigger>
      <DialogContent
        data-error-support
        supportFeedback={false}
        className="sm:max-w-2xl"
        onCloseAutoFocus={(event) => {
          if (opener.current?.isConnected) {
            event.preventDefault();
            opener.current.focus();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle className="pr-6">Como resolver e falar com o suporte</DialogTitle>
          <DialogDescription>
            O código identifica o problema. O identificador de atendimento localiza a tentativa nos
            logs. Nenhum dado do formulário é copiado.
          </DialogDescription>
        </DialogHeader>
        {incidents.map((incident, index) => (
          <section
            key={`${incident.occurredAt}-${index}`}
            className="space-y-3 rounded-lg border p-4 text-sm"
          >
            <h3 className="font-semibold break-words">
              {incident.support.code} · {incident.support.title}
            </h3>
            <p>
              {incident.support.module} · {new Date(incident.occurredAt).toLocaleString('pt-BR')}
            </p>
            <ol className="list-decimal space-y-1 pl-5">
              {incident.support.steps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
            {incident.support.fields.length > 0 && (
              <ul className="space-y-1">
                {incident.support.fields.map((field, i) => (
                  <li key={i}>
                    {field.label}: {field.message} ({field.code})
                  </li>
                ))}
              </ul>
            )}
            <p className="break-all text-xs">
              Atendimento: {incident.requestId ?? 'Sem identificador do servidor'}
            </p>
            <Button
              size="sm"
              variant="outline"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(copyableIncident(incident));
                  setCopyStatus('Detalhes copiados.');
                } catch {
                  setCopyStatus(
                    'A cópia foi bloqueada pelo navegador. Selecione os detalhes abaixo.',
                  );
                }
              }}
            >
              <Copy /> Copiar detalhes para o suporte
            </Button>
            <details>
              <summary className="cursor-pointer">Ver detalhes seguros</summary>
              <pre className="mt-2 whitespace-pre-wrap break-all text-xs">
                {copyableIncident(incident)}
              </pre>
            </details>
          </section>
        ))}
        <p role="status" className="text-sm">
          {copyStatus}
        </p>
        <Button
          variant="ghost"
          onClick={() => {
            supportStore.clear();
            setOpen(false);
          }}
        >
          Limpar avisos desta sessão
        </Button>
      </DialogContent>
    </Dialog>
  );
}
